import React, { useState, useEffect } from 'react';
import { fetchWeatherData } from './dataService';
import { translate, translateWeatherCondition } from './translationService';
import { Link } from 'react-router-dom';

interface AboutProps {
  language: 'en' | 'es' | 'ja';
}

interface WeatherData {
  current?: {
    temp_c?: number;
    temp_f?: number;
    condition?: { text?: string };
  };
}

const DATE_LOCALES: Record<AboutProps['language'], string> = {
  en: 'en-US',
  es: 'es-ES',
  ja: 'ja-JP',
};

const About: React.FC<AboutProps> = ({ language }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [time, setTime] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [abbr, setAbbr] = useState<string>('GMT');

  useEffect(() => {
    let cancelled = false;
    const getWeatherData = async () => {
      try {
        const weatherData = await fetchWeatherData();
        if (!cancelled) setWeather(weatherData);
      } catch {
        if (!cancelled) setWeather(null);
      }
    };
    getWeatherData();
    return () => { cancelled = true; };
  }, []);

  // Tick the clock every second and refresh the date too, so leaving the page
  // open past midnight rolls the date over.
  useEffect(() => {
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Europe/London',
    };

    const tick = () => {
      const now = new Date();
      const londonTime = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false, // 24-hour format
      }).format(now);
      setTime(londonTime);
      // GMT in winter, BST in summer — read it off the formatter so it stays right.
      setAbbr(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          timeZoneName: 'short',
        })
          .formatToParts(now)
          .find((part) => part.type === 'timeZoneName')?.value ?? 'GMT'
      );
      setDate(new Intl.DateTimeFormat(DATE_LOCALES[language], dateOptions).format(now));
    };

    tick(); // set immediately
    const timeInterval = setInterval(tick, 1000);
    return () => clearInterval(timeInterval);
  }, [language]);

  // Localized location label
  const locationLabel: Record<AboutProps['language'], string> = {
    en: 'London, United Kingdom',
    es: 'Londres, Reino Unido',
    ja: 'ロンドン（イギリス）',
  };

  // London runs on Celsius, so every language gets °C.
  const getTemperature = () => {
    if (!weather?.current) return '';
    return `${weather.current.temp_c ?? ''}${translate('tempUnit', language)}`;
  };

  const weatherChunk =
    weather?.current
      ? `${getTemperature()}, ${translateWeatherCondition(
          weather.current?.condition?.text ?? '',
          language
        )}, ${date}`
      : date;

  // Join only the populated segments so we never render a dangling " — ".
  const metaLine = [
    locationLabel[language],
    `${time} ${abbr}`.trim(),
    weatherChunk,
  ].filter(Boolean).join(' — ');

  return (
    <main>
      <section className="about">
        <h1>{translate('infoTitle', language)}</h1>
        <div className="content">
          <img src="/unique-about-page.jpg" alt="Unique Sada" className="about-image" />
          <div className="description">
            <div className="description-top">
              <p className="name"><b>{translate('name', language)}</b></p>
              <p className="jobTitle"><b>{translate('jobTitle', language)}</b></p>
              <p>{translate('aboutText1', language)}</p>
              <p>{translate('aboutText2', language)}</p>
              <p>{translate('aboutText3', language)}</p>
            </div>
            <div className="description-bottom">
              <p className="weather-city">
                <em>{metaLine}</em>
              </p>
              <p>
                <strong>{translate('emailPress', language)}</strong>
                <br />
                <Link to="/contact">{translate('email', language)}</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
